import { useEffect, useState } from 'react'
import { api, imHintergrund, Rechenabbruch } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { anzahlwort, t, textOderRoh } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Dateiwahl } from './Dateiwahl'
import { wertanzeige } from './Stammdaten'
import type {
  Austauschvorschau,
  Austauschzeile,
  Feldwechsel,
  Objektartbeschreibung,
} from './typen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { useDarf } from './Rechte'

/**
 * *Stammdaten → Export und Import* (5.1 Abschnitt 10, R560–R567).
 *
 * **Der Punkt heißt nach seinen zwei Handgriffen und nicht „Austausch"**
 * (Festlegung des Auftraggebers vom 2026-09-15): Das Wort sagt niemandem, was
 * hier geschieht. Die Schaltflächen sagen es noch einmal — *Stammdaten
 * exportieren* und *Stammdaten importieren*.
 *
 * **Drei Schritte, und der dritte schreibt erst nach einer Entscheidung**
 * (R5): Datei wählen, Vorschau lesen, übernehmen.
 */
export function Stammdatenaustausch() {
  const darf = useDarf()
  const [vorschau, setVorschau] = useState<Austauschvorschau | null>(null)
  const [datei, setDatei] = useState<File | null>(null)
  const [gesehen, setGesehen] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [abbruch, setAbbruch] = useState<Rechenabbruch | null>(null)
  /**
   * Ein anderer fachlicher Befund als ein Abbruch des Kerns. Eine Störung
   * meldet das Störungsfenster (7.2 Abschnitt 30, R1003/R1004) — bis dahin
   * lief sie hier unbehandelt aus der Seite.
   */
  const [fehlersatz, setFehlersatz] = useState<string | null>(null)
  // Was jedes Feld ist — damit eine Änderung so dasteht wie im Blatt (R838–R840).
  const [arten, setArten] = useState<Objektartbeschreibung[]>([])

  useEffect(() => {
    // Ohne Beschreibung steht der Wert, wie er kam: kein Abbruch der Seite —
    // ein Nachschlagen im Hintergrund (R1007).
    let gilt = true
    imHintergrund(() => api.stammdatenarten()).then(
      (a) => gilt && setArten(a),
      () => gilt && setArten([]),
    )

    return () => {
      gilt = false
    }
  }, [])

  async function pruefe(gewaehlt: File) {
    setDatei(gewaehlt)
    setVorschau(null)
    setGesehen(false)
    setAbbruch(null)
    setFehlersatz(null)
    setLaeuft(true)

    try {
      const felder = new FormData()
      felder.append('datei', gewaehlt)
      setVorschau(await api.stammdatenVorschau(felder))
    } catch (f) {
      if (f instanceof Rechenabbruch) setAbbruch(f)
      else setFehlersatz(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  async function uebernimm() {
    if (!datei) return
    setAbbruch(null)
    setFehlersatz(null)
    setLaeuft(true)

    try {
      const felder = new FormData()
      felder.append('datei', datei)
      felder.append('rechnendBestaetigt', String(gesehen))
      await api.stammdatenUebernehmen(felder)
      // Ohne „5 angelegt, 2 in neuer Fassung, 10 unverändert. Die Mappe ist
      // archiviert." (7.2 Abschnitt 34, R1131).
      meldeErfolg(t('erfolg.stammdaten.uebernommen'))
      setVorschau(null)
    } catch (f) {
      if (f instanceof Rechenabbruch) setAbbruch(f)
      else setFehlersatz(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  // **Nur was etwas tut, steht in der Liste.** Die Unveränderten werden
  // gezählt und nicht gezeigt: Bei einem sauberen Rundlauf ist „592
  // unverändert, nichts zu tun" die ganze Auskunft, und 592 Zeilen darunter
  // wären Lärm.
  const handelnd = (vorschau?.zeilen ?? []).filter((z) => z.lage !== 'Unveraendert')
  const befunde = handelnd.filter((z) => z.lage === 'Befund')
  const rechnend = handelnd.filter((z) => z.lage !== 'Befund' && z.rechnend.length > 0)
  const uebrige = handelnd.filter((z) => z.lage !== 'Befund' && z.rechnend.length === 0)

  // Der Knopf bleibt gesperrt, solange die rechnenden Änderungen nicht
  // bestätigt sind — und der Grund steht daneben, nicht im Verborgenen (R145).
  const fehltBestaetigung = (vorschau?.rechnend ?? 0) > 0 && !gesehen
  const gesperrt = laeuft || !vorschau || vorschau.befunde > 0 || fehltBestaetigung

  return (
    <section className="austausch">
      <h1>{t('bereich.stammdaten.exportimport')}</h1>

      <div className="austauschgriffe">
        {/* Eine Datei, die der Browser herunterlädt, soll er auch selbst holen
            — derselbe Weg wie bei der Excel-Mappe der Zuordnungen. */}
        <a href="/api/stammdaten/austausch" download>
          {t('austausch.exportieren')}
        </a>

        {/*
          **Ein `label` und kein Knopf, der ein verstecktes Feld anklickt.**
          Die erste Fassung hatte den Umweg über `ref.click()` — und die
          Funktionsprüfung hat ihn gemeldet: ein Knopf, dessen Wirkung nur im
          Browser sichtbar ist und in der Prüfung gar nicht (R451). Das `label`
          löst die Dateiwahl von sich aus aus, ohne eine Zeile JavaScript, und
          das Feld trägt seinen Namen dann selbst.
        */}
        {/* Wer nicht einlesen darf, bekommt nur den Export (R1180). */}
        {darf('stammdatenVorschau') && (
          <Dateiwahl
            beschriftung={t('austausch.importieren')}
            accept=".xlsx"
            ohneNamen
            leeren
            aufWahl={(dateien) => {
              if (dateien[0]) void pruefe(dateien[0])
            }}
          />
        )}
      </div>

      {datei && <p className="erlaeuterung">{t('austausch.datei', { datei: datei.name })}</p>}

      {abbruch && (
        <p className="meldung befund" role="alert">
          {t(abbruch.meldung.schluessel as Sprachschluessel, werte(abbruch.meldung.werte))}{' '}
          {abbruch.handreichung &&
            t(abbruch.handreichung.schluessel as Sprachschluessel, werte(abbruch.handreichung.werte))}
        </p>
      )}

      {fehlersatz && (
        <p className="meldung befund" role="alert">
          {fehlersatz}
        </p>
      )}

      {vorschau && (
        <>
          <div className="austauschzaehler">
            <Zaehler wert={vorschau.neu} was={t('austausch.zaehler.neu')} />
            <Zaehler wert={vorschau.fassungen} was={t('austausch.zaehler.geaendert')} />
            {/* Was unverändert bleibt, zählt nicht (7.2 Abschnitt 36, R1143). */}
            <Zaehler wert={vorschau.befunde} was={t('austausch.zaehler.befunde')} betont={vorschau.befunde > 0} />
          </div>

          {handelnd.length === 0 && <p className="hinweis">{t('austausch.nichtszutun')}</p>}

          {vorschau.uebergangeneBlaetter.length > 0 && (
            <p className="erlaeuterung">
              {t('austausch.uebergangen', { blaetter: vorschau.uebergangeneBlaetter.join(', ') })}
            </p>
          )}

          {/* Eine gestrichene Spalte wird übersprungen und genannt, kein Befund (R1446). */}
          {(vorschau.uebergangeneSpalten ?? []).length > 0 && (
            <p className="erlaeuterung">
              {t('austausch.gestrichen', { angaben: (vorschau.uebergangeneSpalten ?? []).join(', ') })}
            </p>
          )}

          {/* Befunde zuerst: Sie halten die ganze Mappe an (R563). */}
          {befunde.length > 0 && <Liste titel={t('austausch.liste.befunde')} zeilen={befunde} arten={arten} />}

          {rechnend.length > 0 && (
            <>
              <Liste titel={t('austausch.liste.rechnend')} zeilen={rechnend} arten={arten} />
              {/* Die Bestätigung gehört zum Übernehmen und fehlt mit ihm (R1180). */}
              {darf('stammdatenUebernehmen') && (
                <label className="austauschhaken">
                  <input type="checkbox" checked={gesehen} onChange={(e) => setGesehen(e.target.checked)} />
                  {/* Eine Änderung ist keine „1 Änderungen" — der Satz wird gelesen. */}
                  <span>
                    {vorschau.rechnend === 1
                      ? t('austausch.bestaetigen.eine')
                      : t('austausch.bestaetigen', { anzahl: anzahlwort(vorschau.rechnend) })}
                  </span>
                </label>
              )}
            </>
          )}

          {uebrige.length > 0 && <Liste titel={t('austausch.liste.uebrige')} zeilen={uebrige} arten={arten} />}

          {darf('stammdatenUebernehmen') && (
            <div className="austauschgriffe">
              <button type="button" className="haupt" disabled={gesperrt} onClick={() => void uebernimm()}>
                {t('austausch.uebernehmen')}
              </button>
              {vorschau.befunde > 0 && <span className="erlaeuterung">{t('austausch.grund.befunde')}</span>}
              {fehltBestaetigung && <span className="erlaeuterung">{t('austausch.grund.bestaetigung')}</span>}
            </div>
          )}
        </>
      )}
    </section>
  )
}

function Zaehler({ wert, was, betont }: { wert: number; was: string; betont?: boolean }) {
  return (
    <div className={`austauschzahl ${betont ? 'betont' : ''}`.trim()}>
      <span className="zahl">{anzahlwort(wert)}</span>
      <span className="was">{was}</span>
    </div>
  )
}

/**
 * Eine Zeile sagt, **wo sie steht** — Blatt und Zeilennummer —, damit man sie
 * in der Datei wiederfindet. Ein Befund ohne Fundstelle schickt den Anwender
 * suchen (R145).
 */
function Liste({
  titel,
  zeilen,
  arten,
}: {
  titel: string
  zeilen: Austauschzeile[]
  arten: Objektartbeschreibung[]
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('stammdatenaustausch:liste')

  return (
    <details className="austauschliste" open>
      <summary>
        {titel} <span className="anzahl">{anzahlwort(zeilen.length)}</span>
      </summary>
      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('austausch.spalte.stelle')}</th>
              <th>{t('austausch.spalte.code')}</th>
              <th>{t('austausch.spalte.was')}</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={`${z.objektart}|${z.nummer}`}>
                <td className="code">
                  {t(`stammdaten.art.${z.objektart}` as never)} · {t('austausch.zeile', { nummer: String(z.nummer) })}
                </td>
                <td className="code">{z.code || z.kennung || '—'}</td>
                <td>
                  {z.meldung ??
                    (z.rechnend.length > 0
                      ? z.rechnend
                          .map((w) => wechsel(z.objektart, w, arten))
                          .join(' · ')
                      : t(z.lage === 'Neu' ? 'austausch.zeile.neu' : 'austausch.zeile.geaendert'))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

/**
 * Eine Änderung, wie der Anwender sie liest — „Kapitalanteil: 80 % → 75 %"
 * (7.2 Abschnitt 22, R840; R838, R839).
 *
 * **Bis zum 2026-09-30 stand hier „quote: 0.80000000 → 0.75"** — der Name
 * der Spalte und die Speicherform, eine Gesellschaft als Kennung, eine
 * Periode als 202603. Jetzt steht der Name des Feldes und der Wert so wie
 * im Blatt: dieselbe Stelle, die auch Knopf und Fassungsvergleich fragen.
 * Ein Feld, das die Beschreibung nicht kennt — ein Feld des Hauses —, trägt
 * seinen Namen, wie er kam.
 */
function wechsel(objektart: string, w: Feldwechsel, arten: Objektartbeschreibung[]): string {
  const beschreibung = arten
    .find((a) => a.objektart === objektart)
    ?.felder.find((f) => f.feld === w.feld)
  const feld = beschreibung ?? { feld: w.feld, art: 'text' }
  const wert = (roh: string | null) => (roh ? wertanzeige(feld, roh) : '—')

  return `${textOderRoh(`stammdaten.feld.${w.feld}`, w.feld)}: ${wert(w.alt)} → ${wert(w.neu)}`
}

/** Die Werte eines Textbausteins als Ersetzungen. */
function werte(liste: { feld: string; wert: string }[]): Record<string, string> {
  return Object.fromEntries(liste.map((w) => [w.feld, w.wert]))
}
